import { useEffect, useState } from 'react'

import { getDeviceLiveStatus, useConnectorHealth } from './useConnectorHealth'

const VERIFY_POLL_MS = 2000
const VERIFY_TIMEOUT_MS = 90000

export type DeviceVerificationStatus = 'waiting' | 'connected' | 'timeout'

/**
 * After a device is saved, watch the connector until it reports the device
 * connected (or alive), or give up after a generous window so the user gets
 * a clear next step instead of an endless spinner.
 */
export function useDeviceVerification(deviceName: string | null): DeviceVerificationStatus {
  const { health } = useConnectorHealth(deviceName ? VERIFY_POLL_MS : 0)
  const [status, setStatus] = useState<DeviceVerificationStatus>('waiting')
  const [startedAt, setStartedAt] = useState(() => Date.now())

  useEffect(() => {
    setStatus('waiting')
    setStartedAt(Date.now())
  }, [deviceName])

  useEffect(() => {
    if (!deviceName || status !== 'waiting') return
    if (getDeviceLiveStatus(health, { configName: deviceName }) === 'connected') {
      setStatus('connected')
      return
    }
    if (Date.now() - startedAt > VERIFY_TIMEOUT_MS) setStatus('timeout')
  }, [deviceName, health, startedAt, status])

  return status
}
